import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  OnDestroy,
  OnInit,
  AfterViewInit,
  output,
  signal,
  type TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, merge, Subject, switchMap, takeUntil } from 'rxjs';

import { ApiService, AppUserListRow } from '@core/services/api';
import { KlConfirmDialogService } from '@core/services/kl-confirm-dialog.service';
import { ToastService } from '@core/services/toast.service';
import { UserActionAuthService } from '@core/services/user-action-auth.service';
import { UserEnvService } from '@core/services/user-env/user-env.service';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';
import { Menu } from 'primeng/menu';
import type { PaginatorState } from 'primeng/paginator';
import {
  Table,
  TableColResizeEvent,
  TableColumnReorderEvent,
  TableRowCollapseEvent,
  TableRowExpandEvent,
  TableRowSelectEvent,
} from 'primeng/table';
import {
  ActionDto,
  cellRawValue,
  FastFilterDesignRecord,
  FilterableItem,
  GridActionContext,
  GridColumn,
  GridRowCellValue,
  KlStatusBadgeTone,
  InitState,
  isRowObject,
  KlGridExportScope,
} from '../grid.models';
import { copyTextToClipboard, gridRowCopyText, isRowCopyShortcut } from '../../../utils/kl-grid-row-copy';
import { ColumnFilterState } from '../kl-column-filter/kl-column-filter';
import { KlGridColumnSettingsDrawer } from '../kl-grid-column-settings-drawer/kl-grid-column-settings-drawer';
import { KlGridToolbar } from './kl-grid-toolbar/kl-grid-toolbar';

/** Row count past which {@link KlGridContainer.effectiveVirtualScroll} auto-enables DOM windowing. */
export const VIRTUAL_SCROLL_ROW_THRESHOLD = 200;

@Component({
  selector: 'kl-grid-container',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './kl-grid-container.html',
  styleUrl: './kl-grid-container.scss',
})
export class KlGridContainer<T extends object = object> implements OnInit, AfterViewInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly actionAuth = inject(UserActionAuthService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly api = inject(ApiService);
  private readonly userEnv = inject(UserEnvService);
  private readonly confirmDialog = inject(KlConfirmDialogService);
  private readonly toast = inject(ToastService);

  /** Avoid stacking multiple save prompts while one confirm dialog is still open. */
  private designSavePromptOpen = false;
  private destroyed = false;

  /** When true, resizing columns prompts to save widths as grid design (resize works in either state). */
  readonly columnWidthEditMode = signal(false);

  // ─── Signal inputs (readonly from parent) ───
  readonly data = input<T[]>([]);
  readonly columns = input.required<GridColumn[]>();
  readonly authNum = input.required<string>();
  readonly showButton = input(true);
  readonly showToolbar = input(true);
  readonly doAdd = input(true);
  readonly doEdit = input(true);
  readonly doView = input(true);
  readonly doDelete = input(true);
  readonly doRefresh = input(true);
  readonly doExport = input(true);
  readonly doBulkDelete = input(true);
  readonly doOther = input(true);
  readonly disabledButton = input(false);
  readonly deferResizing = input(false);
  readonly sortable = input(true);
  readonly filterable = input(false);
  /** Per-column value checklist filter (independent of toolbar filter bar). */
  readonly columnFilters = input(true);
  /** PrimeNG header drag reorder (same pattern as `kl-report-datatable-results`). */
  readonly columnReorderEnabled = input(true);
  /** PrimeNG column resize handles on headers. */
  readonly columnResizeEnabled = input(true);
  readonly showGridSearch = input(false);
  readonly haveHistory = input(false);
  readonly doInfo = input(true);

  /** `.002`/`.003`/`.004`/`.005` role-permission grants layered on top of each feature's own on/off flag. */
  readonly effectiveDoEdit = computed(() => this.doEdit() && this.actionAuth.canShowAction(`${this.authNum()}.002`));
  readonly effectiveDoView = computed(() => this.doView() && this.actionAuth.canShowAction(`${this.authNum()}.003`));
  readonly effectiveDoDelete = computed(
    () => this.doDelete() && this.actionAuth.canShowAction(`${this.authNum()}.004`)
  );
  readonly effectiveDoBulkDelete = computed(
    () => this.doBulkDelete() && this.actionAuth.canShowAction(`${this.authNum()}.004`)
  );
  readonly effectiveHaveHistory = computed(
    () => this.haveHistory() && this.actionAuth.canShowAction(`${this.authNum()}.005`)
  );
  readonly customFilter = input(false);
  readonly fullscreen = input(true);
  readonly doDesign = input(false);
  readonly multi_select = input(false);
  readonly dblSelect = input(false);
  readonly actions = input<ActionDto[]>([]);
  readonly processesActions = input<ActionDto[]>([]);
  readonly customToolbar = input<ActionDto[]>([]);
  /** When false, hides {@link customToolbar} buttons (parent can still pass an empty array). */
  readonly customToolbarVisible = input(true);
  /** From parent `kl-grid`: refresh current page after row actions (lock, etc.). */
  readonly actionRefresh = input<(() => void) | undefined>(undefined);
  /** From parent `kl-grid`: show/hide grid loading overlay during async row actions. */
  readonly actionGridLoading = input<GridActionContext['gridLoading'] | undefined>(undefined);
  readonly actionShowAlert = input<GridActionContext['showAlert'] | undefined>(undefined);
  readonly actionHandleError = input<GridActionContext['handleError'] | undefined>(undefined);
  readonly initStatesWithoutChild = input<InitState[]>([]);
  readonly initStatesWithChild = input<InitState[]>([]);
  /** {@link initStatesWithoutChild} narrowed to the states the user is actually authorized to add. */
  readonly permittedInitStatesWithoutChild = computed(() =>
    this.initStatesWithoutChild().filter((s) => !s.authNum || this.actionAuth.canShowAction(s.authNum))
  );
  /** {@link doAdd}, also hiding the whole Add control when every configured sub-type was filtered out. */
  readonly effectiveDoAdd = computed(
    () =>
      this.doAdd() && (this.initStatesWithoutChild().length === 0 || this.permittedInitStatesWithoutChild().length > 0)
  );
  readonly gridHeaderName = input('');
  readonly filterSelectItems = input<FilterableItem[]>([]);
  readonly alertShow = input(false);
  readonly alertType = input('error');
  readonly alertMessage = input('');
  readonly autoClose = input(true);
  readonly loading = input(false);
  readonly loadingText = input('loading');
  readonly recordFetchLoading = input(false);

  readonly pageSize = model(10);
  readonly endpointName = input('');
  readonly designParentModule = input('');
  readonly designModule = input('');
  readonly designKey = input('');

  /** Server-side total row count (for the grid paginator). */
  readonly totalRecords = input(0);
  /** Zero-based row offset of the current page (same as API paging.offset). */
  readonly pageFirst = input(0);
  /** Show the server-side paginator under the table. */
  readonly showTablePaginator = input(false);

  /** Forces DOM windowing on even below {@link VIRTUAL_SCROLL_ROW_THRESHOLD}. */
  readonly virtualScroll = input(false);
  /** Fixed row height in px for {@link virtualScroll} (must match the CSS row height). */
  readonly virtualScrollItemSize = input(31);

  /** Optional row-expansion body (`ng-template` from parent via {@link KlGrid}). */
  readonly rowExpansionTemplate = input<TemplateRef<object> | null>(null);

  /** Prime `p-table` `dataKey` — row identity for selection and row expansion keys. */
  readonly gridDataKey = input<string>('id');

  /** Prime `p-table` `rowExpandMode` when row expansion is used. */
  readonly rowExpandMode = input<'single' | 'multiple'>('multiple');

  /** Emitted when the user changes page or page size via the table paginator. */
  readonly tablePageChange = output<{ first: number; rows: number }>();

  // ─── Signal outputs ───
  readonly editCell = output<T>();
  readonly dblClickEditCell = output<T>();
  readonly viewCell = output<T>();
  readonly deleteCell = output<T>();
  readonly bulkDelete = output<T[]>();
  readonly infoCell = output<T>();
  readonly recordHistoryCell = output<T>();
  readonly newRecord = output<InitState | undefined>();
  readonly filterApplied = output<any>();
  readonly customFilterEvent = output<any>();
  readonly refreshGrid = output<void>();
  readonly exportGrid = output<KlGridExportScope>();
  readonly closeAlert = output<void>();
  readonly selectionChange = output<T>();
  readonly rowAction = output<{ action: string | undefined; row: T }>();

  // ─── Local mutable state ───
  /** PrimeNG controlled row expansion (`[expandedRowKeys]`); keys are `String(row[dataKey])`. */
  readonly expandedRowKeys = signal<Record<string, boolean>>({});

  readonly selectedRow = signal<T | null>(null);
  readonly currentItem = signal<any>({});
  readonly showFilterContainer = signal(false);
  readonly bulkDeleteEnabled = signal(false);
  readonly allChecked = signal(false);
  readonly selectedData = signal<any[]>([]);
  readonly sidebarVisible = signal(false);
  readonly drawerInitialTab = signal<'columns' | 'design'>('columns');
  readonly fullscreenActive = signal(false);

  readonly overlayAppendTo = computed(() => (this.fullscreenActive() ? this.host.nativeElement : 'body'));

  /** First load with no rows yet — full loader panel. */
  readonly showInitialLoader = computed(() => this.loading() && this.data().length === 0);

  /** Refresh / actions while rows are visible — overlay on top of the table. */
  readonly showLoadingOverlay = computed(() => this.loading() && this.data().length > 0);

  readonly mergedCustomToolbar = computed((): ActionDto[] => {
    const items: ActionDto[] = [];
    if (this.doDesign()) {
      items.push({
        slug: 'save_design_or_load',
        icon: 'ph ph-sliders',
        gridLevel: true,
        method: () => this.openDesign(),
      });
    }
    const viewSource = this.actions().find((a) => a.slug === 'view_source');
    if (viewSource && this.isActionVisible(viewSource)) {
      items.push({
        ...viewSource,
        icon: viewSource.icon ?? 'ph ph-arrow-square-out',
      });
    }
    return [...items, ...this.customToolbar()];
  });

  readonly textToFilter = signal('');
  readonly filterType = signal<FilterableItem | null>(null);
  readonly filterExpression = signal('LIKE');

  readonly filterExpressionOptions: { value: string; label: string }[] = [
    { value: 'LIKE', label: 'filter_LIKE' },
    { value: 'NOT_LIKE', label: 'filter_NOT_LIKE' },
    { value: 'EQ', label: 'filter_EQ' },
    { value: 'NOT_EQ', label: 'filter_NOT_EQ' },
    { value: 'START_WITH', label: 'filter_START_WITH' },
    { value: 'END_WITH', label: 'filter_END_WITH' },
    { value: 'GT', label: 'filter_GT' },
    { value: 'GE', label: 'filter_GE' },
    { value: 'LT', label: 'filter_LT' },
    { value: 'LE', label: 'filter_LE' },
  ];

  /** Id of the current user's saved fast-filter `design` record, once loaded/saved. */
  private fastFilterRecordId: number | string | null = null;
  readonly fastFilterSaving = signal(false);
  readonly fastFilterMenuModel = computed<MenuItem[]>(() => [
    {
      label: this.translate.instant('save'),
      icon: 'ph ph-floppy-disk',
      command: () => this.saveFastFilter(),
    },
  ]);

  readonly editViewMenuModel = signal<MenuItem[]>([]);
  readonly filterMenuModel = signal<MenuItem[]>([]);
  readonly exportMenuModel = signal<MenuItem[]>([]);
  readonly otherMenuModel = signal<MenuItem[]>([]);
  readonly processesMenuModel = signal<MenuItem[]>([]);
  readonly contextMenuModel = signal<MenuItem[]>([]);
  readonly hasProcessesMenu = signal(false);
  readonly hasOtherMenu = signal(false);

  /** Column filter states keyed by binding (signal for reactivity in filteredData). */
  readonly columnFilterStates = signal(new Map<string, ColumnFilterState>());

  /** Client-side sort from column header menu (current page / loaded rows). */
  readonly clientSortBinding = signal<string | null>(null);
  readonly clientSortDesc = signal(false);

  /** Local column definitions (may differ from input when drawer toggles visibility). */
  readonly effectiveColumns = signal<GridColumn[]>([]);

  /**
   * PrimeNG `p-table` [columns] for reorder (mutated in place). Only columns with `binding`
   * participate; order tracks {@link visibleColumns}.
   */
  readonly tableColumnMeta = signal<{ field: string }[]>([]);

  /** Visible columns (after applying column visibility). */
  readonly visibleColumns = computed(() => this.effectiveColumns().filter((c) => c.visible !== false));

  /** Settings column (gear / row expand) + data columns (empty / expansion `colSpan`). */
  readonly tableLeadColSpan = computed(() => this.visibleColumns().length + 1);

  /** Toolbar Add → menu when multiple {@link initStatesWithoutChild} entries (e.g. document types). */
  readonly addInitStatesMenuModel = computed<MenuItem[]>(() =>
    this.permittedInitStatesWithoutChild().map((s) => ({
      label: this.translate.instant(s.label),
      command: () => this.openNew(s),
    }))
  );

  /** Data filtered by column value-list filters, then client-sorted when requested. */
  readonly filteredData = computed(() => {
    let rows = this.data();
    const states = this.columnFilterStates();
    const activeFilters = Array.from(states.values()).filter((f) => f.active);
    if (activeFilters.length) {
      rows = rows.filter((row) => activeFilters.every((f) => f.checkedValues.has(cellRawValue(row, f.binding))));
    }
    const sortBinding = this.clientSortBinding();
    if (!sortBinding) {
      return rows;
    }
    const colMap = new Map(this.effectiveColumns().map((c) => [c.binding ?? '', c]));
    const col = colMap.get(sortBinding);
    const desc = this.clientSortDesc();
    return [...rows].sort((a, b) => {
      const cmp = this.compareRowsForSort(a, b, sortBinding, col);
      return desc ? -cmp : cmp;
    });
  });

  /** Auto-enables DOM windowing past {@link VIRTUAL_SCROLL_ROW_THRESHOLD} rows. */
  readonly effectiveVirtualScroll = computed(
    () => this.virtualScroll() || this.filteredData().length > VIRTUAL_SCROLL_ROW_THRESHOLD
  );

  /** True when column value filters hide some of the loaded rows. */
  readonly isColumnFiltered = computed(() => this.filteredData().length < this.data().length);

  /** Params for `grid_paginator_report(_filtered)` (left of paginator); does not replace PrimeNG page report. */
  readonly gridPaginatorReportParams = computed(() => {
    const total = this.totalRecords();
    const offset = this.pageFirst();
    const size = this.pageSize();
    const first = total > 0 ? offset + 1 : 0;
    const last = total > 0 ? Math.min(offset + size, total) : 0;
    const loaded = this.data().length;
    const shown = this.filteredData().length;
    return { first, last, total, loaded, shown };
  });

  readonly contextMenuRef = viewChild.required<ContextMenu>('contextMenu');
  readonly columnSettingsDrawer = viewChild<KlGridColumnSettingsDrawer>('columnSettingsDrawer');
  readonly gridToolbar = viewChild<KlGridToolbar>('gridToolbar');
  readonly tableRef = viewChild<Table>('dt');

  /** Passed to the column settings drawer so save snapshots live header widths. */
  readonly captureColumnWidths = (): void => {
    this.syncColumnWidthsFromHeader();
  };

  private readonly destroy$ = new Subject<void>();

  /** PrimeNG `{currentPage} / {totalPages}` template with translated "page" label. */
  readonly pageReportTemplate = computed(() => {
    const label = this.translate.instant('page');
    return `${label} {currentPage} / {totalPages}`;
  });

  constructor() {
    const syncFullscreen = (): void => {
      const active =
        document.fullscreenElement ??
        (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement;
      this.fullscreenActive.set(active === this.host.nativeElement);
    };

    document.addEventListener('fullscreenchange', syncFullscreen);
    document.addEventListener('webkitfullscreenchange', syncFullscreen);

    this.destroyRef.onDestroy(() => {
      document.removeEventListener('fullscreenchange', syncFullscreen);
      document.removeEventListener('webkitfullscreenchange', syncFullscreen);
      if (
        document.fullscreenElement === this.host.nativeElement ||
        (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement ===
          this.host.nativeElement
      ) {
        void this.exitNativeFullscreen();
      }
    });

    effect(() => {
      this.effectiveColumns.set([...this.columns()]);
    });

    effect(() => {
      const vis = this.visibleColumns();
      untracked(() => {
        const keys = vis.map((c) => c.binding).filter((b): b is string => !!b);
        const cur = this.tableColumnMeta().map((m) => m.field);
        if (cur.length === keys.length && cur.every((k, i) => k === keys[i])) {
          return;
        }
        this.tableColumnMeta.set(keys.map((field) => ({ field })));
      });
    });

    effect(() => {
      const items = this.filterSelectItems();
      if (items?.length && !this.filterType()) {
        this.filterType.set(items[0]);
      }
    });

    effect(() => {
      const rows = this.data();
      const hasExpansion = this.rowExpansionTemplate();
      if (!hasExpansion) {
        return;
      }
      untracked(() => {
        if (!rows?.length && Object.keys(this.expandedRowKeys()).length > 0) {
          this.expandedRowKeys.set({});
        }
      });
    });

    effect(() => {
      const rows = this.data();
      if (rows?.length) {
        this.currentItem.set(rows[0]);
        this.selectedRow.set(rows[0]);
        this.selectionChange.emit(rows[0]);
      }
    });

    effect(() => {
      void this.actions();
      void this.processesActions();
      void this.customToolbar();
      void this.doDesign();
      this.buildMenuItems();
      this.cdr.markForCheck();
    });
  }

  /** Toolbar / context menu entry for a row action (flat or with nested `children`). */
  private isActionVisible(action: ActionDto): boolean {
    if (action.isRowVisible && !action.isRowVisible(this.currentItem())) {
      return false;
    }
    if (!action.authNum) {
      return true;
    }
    return this.actionAuth.canShowAction(action.authNum);
  }

  private actionToMenuItem(a: ActionDto, defaultIcon?: string): MenuItem {
    if (a.children?.length) {
      return {
        label: this.translate.instant(a.slug_2 ?? a.slug ?? ''),
        icon: a.icon ?? defaultIcon,
        items: a.children.map((c) => ({
          label: this.translate.instant(c.slug_2 ?? c.slug ?? ''),
          icon: c.icon ?? defaultIcon ?? 'ph ph-list',
          command: () => this.doOtherAction(a, c),
        })),
      };
    }
    return {
      label: this.translate.instant(a.slug_2 ?? a.slug ?? ''),
      icon: a.icon ?? defaultIcon,
      command: () => this.doOtherAction(a),
    };
  }

  ngOnInit(): void {
    const items = this.filterSelectItems();
    if (items?.length && !this.filterType()) {
      this.filterType.set(items[0]);
    }
    this.buildMenuItems();
    this.loadFastFilter();

    merge(this.translate.onLangChange, this.translate.onTranslationChange)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.buildMenuItems();
        this.cdr.markForCheck();
      });
  }

  ngAfterViewInit(): void {
    if (this.hasDesignSaveTarget()) {
      this.columnSettingsDrawer()?.loadInitialGridDesign();
    }
  }

  /** Primary key for toolbar CRUD: `id` if set, else value at {@link gridDataKey} (supports dotted paths). */
  private currentItemEntityId(): GridRowCellValue {
    const row = this.currentItem();
    if (!isRowObject(row)) {
      return undefined;
    }
    const direct = row['id'];
    if (direct != null && direct !== '') {
      return direct;
    }
    const key = this.gridDataKey();
    if (key && key !== 'id') {
      return this.resolveDataKeyValue(row, key);
    }
    return undefined;
  }

  /** Nested property read for {@link gridDataKey} (e.g. `id` or `entity.id`). */
  private resolveDataKeyValue(
    row: Record<string, GridRowCellValue> | null | undefined,
    keyPath: string
  ): GridRowCellValue {
    if (!row || !keyPath) {
      return undefined;
    }
    const parts = keyPath.split('.');
    let cur: GridRowCellValue = row;
    for (const p of parts) {
      if (!isRowObject(cur)) {
        return undefined;
      }
      cur = cur[p];
    }
    return cur;
  }

  /**
   * Unique id for a grid row, used to enable checkboxes and match selections.
   * Uses `row.id` when present; otherwise reads the field named by {@link gridDataKey}
   * (e.g. `transaction_message_query_id` on restaurant end-of-day).
   */
  rowEntityId(row: object | null | undefined): GridRowCellValue {
    if (!isRowObject(row)) {
      return undefined;
    }
    const direct = row['id'];
    if (direct != null && direct !== '') {
      return direct;
    }
    const key = this.gridDataKey();
    if (key && key !== 'id') {
      return this.resolveDataKeyValue(row, key);
    }
    return undefined;
  }

  /**
   * Form field name for the row checkbox column (header select-all uses the same field).
   * Returns the first visible column with `multi_select: true`, e.g. `selected` or `bulkDeleteSelection`.
   */
  private multiSelectColumnBinding(): string | null {
    const col = this.visibleColumns().find((c) => c.multi_select && c.binding);
    return col?.binding ?? null;
  }

  private rowExpansionKey(row: object): string | null {
    if (!isRowObject(row)) {
      return null;
    }
    const path = this.gridDataKey();
    const v = this.resolveDataKeyValue(row, path);
    if (v === undefined || v === null) {
      return null;
    }
    return String(v);
  }

  onRowExpand(event: TableRowExpandEvent): void {
    if (!this.rowExpansionTemplate()) {
      return;
    }
    const k = this.rowExpansionKey(event.data);
    if (!k) {
      return;
    }
    const next = this.rowExpandMode() === 'single' ? { [k]: true } : { ...this.expandedRowKeys(), [k]: true };
    this.expandedRowKeys.set(next);
  }

  onRowCollapse(event: TableRowCollapseEvent): void {
    if (!this.rowExpansionTemplate()) {
      return;
    }
    const k = this.rowExpansionKey(event.data);
    if (!k) {
      return;
    }
    const next = { ...this.expandedRowKeys() };
    delete next[k];
    this.expandedRowKeys.set(next);
  }

  onTablePage(event: PaginatorState): void {
    if (this.rowExpansionTemplate() && Object.keys(this.expandedRowKeys()).length > 0) {
      this.expandedRowKeys.set({});
    }
    this.tableRef()?.resetScrollTop();
    this.tablePageChange.emit({
      first: event.first ?? 0,
      rows: event.rows ?? this.pageSize(),
    });
  }

  onColReorder(_event: TableColumnReorderEvent): void {
    if (!this.columnReorderEnabled()) {
      return;
    }
    const vis = this.visibleColumns();
    const newBindings = this.tableColumnMeta().map((m) => m.field);
    const full = [...this.effectiveColumns()];
    const map = new Map(full.filter((c) => c.binding).map((c) => [c.binding!, c] as const));

    let bi = 0;
    const rebuiltVis = vis.map((c) => {
      if (!c.binding) {
        return c;
      }
      const next = map.get(newBindings[bi++]);
      return next ?? c;
    });

    let vi = 0;
    const merged = full.map((c) => {
      if (c.visible === false) {
        return c;
      }
      return rebuiltVis[vi++]!;
    });

    this.effectiveColumns.set(merged);
    this.columnSettingsDrawer()?.markLiveLayoutTouched();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Row selection ───

  onRowSelect(event: TableRowSelectEvent<T>): void {
    this.currentItem.set(event.data ?? event);
    this.selectedRow.set(this.currentItem());
    this.selectionChange.emit(this.currentItem());
  }

  onRowDblClick(row: T): void {
    this.currentItem.set(row);
    this.selectedRow.set(row);
    this.dblClickEditCell.emit(this.currentItem());
  }

  onRowKeydown(event: KeyboardEvent, row: object): void {
    if (!isRowCopyShortcut(event)) return;
    void copyTextToClipboard(gridRowCopyText(row, this.visibleColumns(), this.translate));
  }

  onRowContextMenu(event: MouseEvent, row: T): void {
    this.currentItem.set(row);
    this.selectedRow.set(row);
    this.buildMenuItems();
    this.cdr.markForCheck();
    if (this.contextMenuModel().length > 0) {
      event.preventDefault();
      setTimeout(() => this.contextMenuRef()?.show(event), 0);
    }
  }

  // ─── Column filter handling ───

  getColumnFilterState(col: GridColumn): ColumnFilterState | null {
    return this.columnFilterStates().get(col.binding!) ?? null;
  }

  onColumnFilterApply(state: ColumnFilterState): void {
    const next = new Map(this.columnFilterStates());
    next.set(state.binding, state);
    this.columnFilterStates.set(next);
  }

  onColumnFilterClear(binding: string): void {
    const next = new Map(this.columnFilterStates());
    next.delete(binding);
    this.columnFilterStates.set(next);
  }

  onColumnSortAsc(binding: string): void {
    this.clientSortBinding.set(binding);
    this.clientSortDesc.set(false);
  }

  onColumnSortDesc(binding: string): void {
    this.clientSortBinding.set(binding);
    this.clientSortDesc.set(true);
  }

  private compareRowsForSort(a: object, b: object, binding: string, col: GridColumn | undefined): number {
    if (!isRowObject(a) || !isRowObject(b)) {
      return 0;
    }
    const va = cellRawValue(a, binding);
    const vb = cellRawValue(b, binding);
    if (va == null && vb == null) return 0;
    if (va == null) return -1;
    if (vb == null) return 1;
    if (col?.format === 'date' || col?.format === 'date-time') {
      const da = new Date(va as string | number | Date).getTime();
      const db = new Date(vb as string | number | Date).getTime();
      if (Number.isNaN(da) || Number.isNaN(db)) {
        return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      }
      return da === db ? 0 : da < db ? -1 : 1;
    }
    if (typeof va === 'number' && typeof vb === 'number') {
      return va === vb ? 0 : va < vb ? -1 : 1;
    }
    return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
  }

  // ─── Toolbar actions ───

  openNew(initState?: InitState): void {
    this.closeActionMenus();
    this.newRecord.emit(initState);
  }

  private forceCloseMenuOverlay(overlay: Menu | ContextMenu | undefined): void {
    if (!overlay) return;
    overlay.hide();
    // PrimeNG popup overlays only clean up when the leave animation completes.
    // When navigation detaches this view mid-animation (LayoutRouteReuseStrategy),
    // the overlay is stranded in <body>. Force-remove it now.
    const container = overlay.container;
    container?.parentNode?.removeChild(container);
    overlay.container = undefined;
  }

  private closeActionMenus(): void {
    const toolbar = this.gridToolbar();
    this.forceCloseMenuOverlay(toolbar?.otherMenu);
    this.forceCloseMenuOverlay(toolbar?.processesMenu);
    this.forceCloseMenuOverlay(toolbar?.editViewMenu);
    this.forceCloseMenuOverlay(toolbar?.filterMenu);
    this.forceCloseMenuOverlay(toolbar?.exportMenu);
    this.forceCloseMenuOverlay(toolbar?.addInitStatesMenu);
    this.forceCloseMenuOverlay(this.contextMenuRef());
  }

  explorerEdit(): void {
    this.closeActionMenus();
    if (this.recordFetchLoading()) return;
    if (this.currentItemEntityId() != null) this.editCell.emit(this.currentItem());
  }

  explorerView(): void {
    this.closeActionMenus();
    if (this.currentItemEntityId() != null) this.viewCell.emit(this.currentItem());
  }

  explorerDelete(): void {
    this.closeActionMenus();
    if (this.currentItemEntityId() != null) this.deleteCell.emit(this.currentItem());
  }

  explorerInfo(): void {
    this.closeActionMenus();
    if (this.currentItemEntityId() != null) this.infoCell.emit(this.currentItem());
  }

  explorerRecordHistory(): void {
    this.closeActionMenus();
    if (this.currentItemEntityId() != null) this.recordHistoryCell.emit(this.currentItem());
  }

  refresh(): void {
    this.refreshGrid.emit();
  }

  submit(): void {
    this.filterApplied.emit(this.prepareFilterObject());
  }

  // ─── Fast filter (search field / operator / page size) save & restore ──────

  private fastFilterCompanyId(): number | null {
    const id = this.userEnv.env()?.company?.id;
    if (id == null) return null;
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
  }

  fastFilterQueryActive(): boolean {
    return !!this.designModule().trim();
  }

  /** Design key of this grid's fast filter (`<module>_FAST_FILTER`). */
  private fastFilterKey(): string {
    return `${this.designModule().trim()}_FAST_FILTER`;
  }

  /** Restores the user's saved search field / operator / page size for this grid, then re-fetches. */
  private loadFastFilter(): void {
    if (!this.fastFilterQueryActive()) return;
    // kl-grid skips its own initial fetch here, so submit() must fire on every path (not just found).
    const companyId = this.fastFilterCompanyId();
    if (!companyId) {
      this.submit();
      return;
    }

    const module = this.designModule().trim();
    const key = this.fastFilterKey();
    this.api
      .getFilteredData<FastFilterDesignRecord[]>('design', {
        parent: { module: this.designParentModule().trim() },
        module,
        key,
        type: 'FILTER',
        company_id: companyId,
      })
      .subscribe({
        next: (res) => {
          // The response can also hold the module's grid designs; saving must never overwrite one of them.
          const record = res?.data?.find(
            (row) => row.module === module && row.key === key && (!row.type || row.type === 'FILTER')
          );
          if (record) {
            this.fastFilterRecordId = record.id ?? null;
            const content = this.parseFastFilterContent(record.content ?? '');
            if (content) {
              // Items can share a name, so match the distinguishing key first.
              const items = this.filterSelectItems();
              const expressionName = content.filter_expression_name;
              const match =
                items.find((f) => expressionName && (f.parameterName || f.name) === expressionName) ??
                items.find((f) => f.name === content.filter_property);
              if (match) this.filterType.set(match);
              if (content.filter_expression_operator) this.filterExpression.set(content.filter_expression_operator);
              // filter_limit can come back as a string after JSON round-trip.
              const limit = Number(content.filter_limit);
              if (Number.isFinite(limit) && limit > 0) this.pageSize.set(limit);
            }
          }
          this.submit();
        },
        error: () => this.submit(),
      });
  }

  private parseFastFilterContent(content: string): {
    filter_property?: string;
    filter_expression_name?: string;
    filter_expression_operator?: string;
    filter_limit?: number;
  } | null {
    try {
      return JSON.parse(content);
    } catch {
      return null;
    }
  }

  /** Persists the current search field / operator / page size choice for the current user. */
  saveFastFilter(): void {
    if (this.fastFilterSaving() || !this.fastFilterQueryActive()) return;
    const companyId = this.fastFilterCompanyId();
    if (!companyId) return;

    const ft = this.filterType();
    const content = JSON.stringify({
      filter_property: ft?.name,
      filter_expression_name: ft ? ft.parameterName || ft.name : undefined,
      filter_expression_operator: ft && !ft.disabledExpression ? this.filterExpression() : undefined,
      filter_limit: this.pageSize(),
    });
    const key = this.fastFilterKey();

    this.fastFilterSaving.set(true);
    // Fast filters are saved per user: the backend only returns `USER` records listing the requester's id.
    this.api
      .getAllData<AppUserListRow>('user/my')
      .pipe(
        switchMap((me) => {
          const payload = {
            parent: { module: this.designParentModule().trim() },
            module: this.designModule().trim(),
            key,
            level: 'USER',
            level_values: [String(me.id)],
            level_default: 'DEFAULT',
            description: key,
            type: 'FILTER',
            company_id: companyId,
            content,
          };
          return this.fastFilterRecordId
            ? this.api.updateData<FastFilterDesignRecord>('design', { ...payload, id: this.fastFilterRecordId })
            : this.api.addData<FastFilterDesignRecord>('design', payload);
        }),
        finalize(() => this.fastFilterSaving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (saved) => {
          this.fastFilterRecordId = saved?.id ?? this.fastFilterRecordId;
          this.toast.success({ detail: 'success_message' });
        },
        error: (err) => this.actionHandleError()?.(err),
      });
  }

  prepareFilterObject(): any {
    const filterInstance: any = {
      filter: {},
      parameters: [],
      query: { paging: { limit: this.pageSize() || 10, fetch: 'EAGER' } },
    };

    const ft = this.filterType();
    const txt = this.textToFilter();
    if (ft && txt) {
      const name = ft.name;
      const key = ft.objectKey;
      const type = ft.type;

      if (key) {
        if (type === 'array') {
          filterInstance.filter[name] = [{ [key]: txt }];
        } else if (type === 'object') {
          filterInstance.filter[name] = { [key]: txt };
        } else {
          filterInstance.filter[name] = filterInstance.filter[name] || {};
          filterInstance.filter[name][key] = txt;
        }
      } else {
        filterInstance.filter[name] = type === 'number' ? parseInt(txt, 10) : txt;
      }
    }

    const expr = this.filterExpression();
    if (expr && ft && !ft.disabledExpression) {
      filterInstance.parameters.push({
        name: ft.parameterName || ft.name,
        expression: { operator: expr },
      });
    }

    return {
      filterInstance,
      filterArgs: {
        type: ft,
        expression: expr,
        size: this.pageSize(),
      },
    };
  }

  exportAs(scope: KlGridExportScope): void {
    this.closeActionMenus();
    this.exportGrid.emit(scope);
  }

  toggleFilterContainer(): void {
    this.showFilterContainer.set(!this.showFilterContainer());
  }

  customFilterOpen(): void {
    this.closeActionMenus();
    this.customFilterEvent.emit({ customFilter: true, pageSize: this.pageSize() });
  }

  buildMenuItems(): void {
    this.editViewMenuModel.set([
      {
        label: this.translate.instant('view'),
        command: () => this.explorerView(),
      },
    ]);

    this.filterMenuModel.set([
      {
        label: this.translate.instant('detail_filter'),
        icon: 'ph ph-funnel',
        command: () => this.customFilterOpen(),
      },
    ]);

    this.exportMenuModel.set([
      {
        label: this.translate.instant('export_filtered'),
        icon: 'ph ph-file-xls',
        command: () => this.exportAs('page'),
      },
      {
        label: this.translate.instant('export_all'),
        icon: 'ph ph-file-xls',
        command: () => this.exportAs('all'),
      },
    ]);

    const otherItems: MenuItem[] = [];
    if (this.doInfo() && !this.haveHistory()) {
      otherItems.push({
        label: this.translate.instant('entry_info'),
        command: () => this.explorerInfo(),
      });
    }
    if (this.doInfo() && this.effectiveHaveHistory()) {
      otherItems.push({
        label: this.translate.instant('record_history'),
        command: () => this.explorerRecordHistory(),
      });
    }
    const toolbarActions = (this.actions() ?? []).filter((a) => !a.notShowInToolbar && this.isActionVisible(a));
    for (const a of toolbarActions) {
      if (a.separator) {
        otherItems.push({ separator: true });
      } else {
        otherItems.push(this.actionToMenuItem(a));
      }
    }
    this.otherMenuModel.set(otherItems);
    this.hasOtherMenu.set(this.doOther() && otherItems.length > 0);

    const processesItems: MenuItem[] = [];
    const proc = this.processesActions() ?? [];
    for (const a of proc) {
      if (!a.notShowInToolbar && this.isActionVisible(a)) {
        if (a.separator) {
          processesItems.push({ separator: true });
        } else {
          processesItems.push(this.actionToMenuItem(a));
        }
      }
    }
    this.processesMenuModel.set(processesItems);
    this.hasProcessesMenu.set(processesItems.length > 0);

    const ctxItems: MenuItem[] = [];
    if (this.effectiveDoEdit()) {
      ctxItems.push({
        label: this.translate.instant('edit'),
        icon: 'ph ph-pencil-simple',
        command: () => this.explorerEdit(),
      });
    }
    if (this.effectiveDoView()) {
      ctxItems.push({
        label: this.translate.instant('view'),
        icon: 'ph ph-eye',
        command: () => this.explorerView(),
      });
    }
    if (this.effectiveDoDelete()) {
      ctxItems.push({
        label: this.translate.instant('delete'),
        icon: 'ph ph-trash',
        command: () => this.explorerDelete(),
      });
    }
    if (this.effectiveDoBulkDelete()) {
      ctxItems.push({
        label: this.translate.instant('bulk_delete'),
        icon: 'ph ph-trash',
        command: () => this.startBulkDelete(),
      });
    }
    if (ctxItems.length > 0) ctxItems.push({ separator: true });
    if (this.doInfo() && !this.haveHistory()) {
      ctxItems.push({
        label: this.translate.instant('entry_info'),
        icon: 'ph ph-info',
        command: () => this.explorerInfo(),
      });
    }
    if (this.doInfo() && this.effectiveHaveHistory()) {
      ctxItems.push({
        label: this.translate.instant('record_history'),
        icon: 'ph ph-clock-counter-clockwise',
        command: () => this.explorerRecordHistory(),
      });
    }
    const contextActions = (this.actions() ?? []).filter((a) => !a.notShowInContextMenu && this.isActionVisible(a));
    for (const a of contextActions) {
      if (a.separator) {
        ctxItems.push({ separator: true });
      } else {
        ctxItems.push(this.actionToMenuItem(a, 'ph ph-list'));
      }
    }
    const processContextActions = (this.processesActions() ?? []).filter(
      (a) => !a.notShowInContextMenu && this.isActionVisible(a)
    );
    if (processContextActions.length > 0 && ctxItems.length > 0) {
      ctxItems.push({ separator: true });
    }
    for (const a of processContextActions) {
      if (a.separator) {
        ctxItems.push({ separator: true });
      } else {
        ctxItems.push(this.actionToMenuItem(a, 'ph ph-list'));
      }
    }
    this.contextMenuModel.set(ctxItems);
  }

  onAlertClose(): void {
    this.closeAlert.emit();
  }

  onRowButtonClick(column: GridColumn, row: T, event: MouseEvent): void {
    event.stopPropagation();
    if (!row) return;
    this.rowAction.emit({ action: column.rowActionName, row });
  }

  startBulkDelete(): void {
    const hasCheckbox = this.effectiveColumns().some(
      (c) => c.binding === 'bulkDeleteSelection' || c.binding === 'selected'
    );

    if (!hasCheckbox) {
      const checkboxColumn: GridColumn = {
        header: 'select',
        binding: 'bulkDeleteSelection',
        width: '60px',
        visible: true,
        multi_select: true,
      };
      this.effectiveColumns.set([checkboxColumn, ...this.effectiveColumns()]);
    }

    this.bulkDeleteEnabled.set(true);
    this.selectedData.set([]);
    this.allChecked.set(false);
  }

  confirmBulkDelete(): void {
    this.bulkDelete.emit(this.selectedData());
  }

  cancelBulkDelete(): void {
    this.bulkDeleteEnabled.set(false);
    this.selectedData.set([]);
    this.allChecked.set(false);

    const filtered = this.effectiveColumns().filter((c) => c.binding !== 'bulkDeleteSelection');
    this.effectiveColumns.set(filtered);

    this.cdr.markForCheck();
  }

  toggleAllCheckboxes(): void {
    const binding = this.multiSelectColumnBinding();
    if (!binding) {
      return;
    }

    const rows = this.data();
    const checked = this.allChecked();
    const selected: T[] = [];

    rows.forEach((row) => {
      if (this.rowEntityId(row) == null) {
        return;
      }
      this.setRowFlag(row, binding, checked);
      if (checked) {
        selected.push(row);
      }
    });

    this.selectedData.set(selected);
    this.cdr.markForCheck();
    this.selectionChange.emit(this.currentItem());
  }

  onCheckboxChange(event: { checked?: boolean }, row: T): void {
    const binding = this.multiSelectColumnBinding();
    if (!binding) {
      return;
    }

    const entityId = this.rowEntityId(row);
    if (entityId == null) {
      return;
    }

    const current = [...this.selectedData()];
    if (event.checked) {
      const exists = current.some((r) => this.rowEntityId(r) === entityId);
      if (!exists) {
        current.push(row);
      }
    } else {
      const index = current.findIndex((r) => this.rowEntityId(r) === entityId);
      if (index !== -1) {
        current.splice(index, 1);
      }
      this.allChecked.set(false);
    }

    this.selectedData.set(current);

    const selectable = this.data().filter((r) => this.rowEntityId(r) != null);
    const allSelected = selectable.length > 0 && selectable.every((r) => !!cellRawValue(r, binding));
    if (allSelected) {
      this.allChecked.set(true);
    }

    this.cdr.markForCheck();
    this.selectionChange.emit(this.currentItem());
  }

  /** Writes the checkbox column value; `binding` is a runtime column name on any row shape. */
  private setRowFlag(row: T, binding: string, checked: boolean): void {
    (row as Record<string, boolean>)[binding] = checked;
  }

  onCustomToolbarAction(action: ActionDto): void {
    if (action.gridLevel) {
      this.closeActionMenus();
      action.method?.({ item: this.currentItem() ?? {} });
      return;
    }
    this.doOtherAction(action);
  }

  doOtherAction(action: ActionDto, childAction?: ActionDto): void {
    this.closeActionMenus();
    const item = this.currentItem();
    if (!item || Object.keys(item).length === 0) return;
    const refresh = this.actionRefresh();
    const gridLoading = this.actionGridLoading();
    const showAlert = this.actionShowAlert();
    const handleError = this.actionHandleError();
    const ctxBase: GridActionContext = {
      item,
      ...(refresh ? { refresh } : {}),
      ...(gridLoading ? { gridLoading } : {}),
      ...(showAlert ? { showAlert } : {}),
      ...(handleError ? { handleError } : {}),
    };
    if (!action.children) {
      if (!action.separator) action.method?.(ctxBase);
    } else if (childAction) {
      childAction.method?.(ctxBase);
    }
  }

  preventNonNumeric(e: KeyboardEvent): void {
    const allowed = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'];
    if (!/^\d$/.test(e.key) && !allowed.includes(e.key)) e.preventDefault();
  }

  commitPageSize(e: Event): void {
    const raw = Number((e.target as HTMLInputElement).value);
    const value = Math.max(1, Math.min(999, raw || this.pageSize()));
    this.pageSize.set(value);
  }

  refreshFlexGrid(): void {
    this.cdr.markForCheck();
  }

  openSidebar(tab: 'columns' | 'design' = 'columns'): void {
    this.drawerInitialTab.set(tab);
    this.sidebarVisible.set(true);
  }

  setColumnWidthEditMode(enabled: boolean): void {
    this.columnWidthEditMode.set(enabled);
  }

  openDesign(): void {
    this.openSidebar('design');
  }

  toggleFullscreen(): void {
    if (this.fullscreenActive()) {
      void this.exitNativeFullscreen();
      return;
    }

    const el = this.host.nativeElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void> | void;
    };
    if (el.requestFullscreen) {
      void el.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      void el.webkitRequestFullscreen();
    }
  }

  private exitNativeFullscreen(): Promise<void> | void {
    const doc = document as Document & {
      webkitExitFullscreen?: () => Promise<void> | void;
    };
    if (document.exitFullscreen) {
      return document.exitFullscreen();
    }
    if (doc.webkitExitFullscreen) {
      return doc.webkitExitFullscreen();
    }
  }

  onColumnDrawerResetToDefault(): void {
    this.effectiveColumns.set([...this.columns()]);
    this.columnFilterStates.set(new Map());
    this.clientSortBinding.set(null);
    this.clientSortDesc.set(false);
    this.clearColumnWidthsFromTable();
    queueMicrotask(() => this.columnSettingsDrawer()?.syncAfterParentReset());
  }

  onColumnsChanged(updatedColumns: GridColumn[]): void {
    this.effectiveColumns.set(updatedColumns);
    // The initial design fetch is not cancelled on destroy, so it can answer after the tab
    // closed — registering a render hook on the destroyed injector would throw.
    if (this.destroyed) {
      return;
    }
    // Widths are enforced from the rendered header, so wait for the new cells: visibility
    // and order changes shift the `th:nth-child(n)` indices PrimeNG writes rules against.
    afterNextRender(() => this.applyColumnWidthsToTable(), { injector: this.injector });
  }

  onColResize(_event: TableColResizeEvent): void {
    this.syncColumnWidthsFromHeader();
    this.promptSaveDesignAfterResize();
  }

  /**
   * Copy live header pixel widths from the DOM into {@link effectiveColumns}.
   *
   * Legacy `form-entry-grid` persisted every column width at save time; PrimeNG
   * `p-table` column resize only updates rendered `<th>` sizes, so the in-memory
   * column model can lag behind what the user sees. This method closes that gap by
   * reading `offsetWidth` from each `th.kl-grid-header-cell` in header order and
   * writing the values back onto the matching bindings in `effectiveColumns`.
   *
   * Used from:
   * - {@link onColResize} immediately after the user drags a resize handle
   * - {@link captureColumnWidths} (passed to the column settings drawer) right before
   *   a grid design is saved, so the payload includes all current widths—not only
   *   the column that was last resized
   *
   * Header cells are matched to {@link visibleColumns} by index (lead checkbox /
   * gear column is excluded from `visibleColumns`, so indices stay aligned with
   * data headers). Widths of `*` / flex columns are stored as numeric pixels once
   * measured. No-op when the table is not rendered yet or nothing changed.
   */
  syncColumnWidthsFromHeader(): void {
    const headerCells = Array.from(
      this.host.nativeElement.querySelectorAll('thead tr th.kl-grid-header-cell')
    ) as HTMLElement[];
    const vis = this.visibleColumns();
    if (headerCells.length === 0 || vis.length === 0) {
      return;
    }

    const cols = [...this.effectiveColumns()];
    let changed = false;

    for (const [index, headerCell] of headerCells.entries()) {
      const col = vis[index];
      if (!col?.binding) {
        continue;
      }

      const width = headerCell.offsetWidth;
      if (!Number.isFinite(width) || width <= 0) {
        continue;
      }

      const idx = cols.findIndex((c) => c.binding === col.binding);
      if (idx < 0) {
        continue;
      }

      const current = this.resolveColumnWidth(cols[idx].width);
      if (current !== width) {
        cols[idx] = { ...cols[idx], width };
        changed = true;
      }
    }

    if (changed) {
      this.effectiveColumns.set(cols);
      this.columnSettingsDrawer()?.markLiveLayoutTouched();
    }
  }

  /**
   * Enforce {@link effectiveColumns} widths the way PrimeNG does after a manual resize.
   *
   * `p-table` does not size columns from the `th` `[style.width]` binding: in
   * `columnResizeMode="expand"` it injects a stylesheet of
   * `th:nth-child(n) { width: Xpx !important }` rules and sets an explicit width on the
   * `<table>` element (`Table.onColumnResizeEnd`, replayed by `Table.restoreColumnWidths`
   * for stateful tables). Without that, `table-layout: auto` redistributes the available
   * width and a restored grid design renders as if it had been ignored. Driving PrimeNG's
   * own restore path keeps a design applied from the drawer identical to a drag-resize.
   */
  private applyColumnWidthsToTable(): void {
    const table = this.tableRef();
    if (!table?.tableViewChild?.nativeElement) {
      return;
    }

    // Positional over the header row to match PrimeNG's `th:nth-child(n)` rules: index 0 is
    // the leading gear / row-expand column, which `visibleColumns` does not include.
    const headerRow: HTMLTableRowElement | null = (this.host.nativeElement as HTMLElement).querySelector('thead tr');
    const headerCells = Array.from(headerRow?.cells ?? []) as HTMLElement[];
    if (headerCells.length === 0) {
      return;
    }

    const vis = this.visibleColumns();
    const widths = headerCells.map((cell, index) => {
      const col = index === 0 ? undefined : vis[index - 1];
      return this.resolveColumnWidth(col?.width) ?? cell.offsetWidth;
    });
    // A zero width would be enforced with `!important` and collapse the column for good.
    if (widths.some((width) => !Number.isFinite(width) || width <= 0)) {
      return;
    }

    // `restoreColumnWidths()` only creates a style element, so drop the previous one first.
    table.destroyStyleElement();
    table.columnWidthsState = widths.join(',');
    table.tableWidthState = String(widths.reduce((total, width) => total + width, 0));
    table.restoreColumnWidths();
  }

  /** Drops enforced widths so the table falls back to auto layout (drawer reset to default). */
  private clearColumnWidthsFromTable(): void {
    const table = this.tableRef();
    if (!table?.tableViewChild?.nativeElement) {
      return;
    }
    table.destroyStyleElement();
    table.columnWidthsState = undefined;
    table.tableWidthState = undefined;
    table.setResizeTableWidth('');
  }

  /**
   * Whether this grid can persist column layout to the backend.
   * Supports both modern endpoint-based designs (`endpointName`) and legacy
   * module/key pairs (`designModule` + `designKey`, e.g. payment plan list).
   */
  private hasDesignSaveTarget(): boolean {
    if (this.endpointName().trim()) {
      return true;
    }
    return !!this.designModule().trim() && !!this.designKey().trim();
  }

  /**
   * After a column resize, ask the user whether to save the grid design (legacy UX).
   *
   * Shown on every resize while a save target exists. Only one confirm dialog is
   * allowed at a time (`designSavePromptOpen`). Confirm opens
   * {@link KlGridColumnSettingsDrawer.promptSaveDesign} (preference dialog for legacy
   * grids); dismiss leaves the new widths in {@link effectiveColumns} until the user
   * saves manually or reloads.
   */
  private promptSaveDesignAfterResize(): void {
    if (!this.columnWidthEditMode() || this.designSavePromptOpen || !this.hasDesignSaveTarget()) {
      return;
    }
    this.designSavePromptOpen = true;
    this.confirmDialog
      .open({
        title: 'column_width_changed',
        description: 'save_grid_design_question',
        headerLabel: 'save_design',
        confirmLabel: 'save_design',
        icon: 'ph ph-floppy-disk',
        tone: 'info',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ok) => {
        this.designSavePromptOpen = false;
        if (ok) {
          this.columnSettingsDrawer()?.promptSaveDesign();
        }
      });
  }

  private resolveColumnWidth(width?: number | string): number | undefined {
    if (typeof width === 'number' && Number.isFinite(width)) {
      return width;
    }
    if (typeof width === 'string' && width !== '*' && width.trim() !== '') {
      const parsed = Number.parseInt(width.replace(/px$/i, '').trim(), 10);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
  }

  statusBadgeTone(row: object, col: GridColumn): KlStatusBadgeTone | null {
    if (!col.badgeTones || !col.binding) {
      return null;
    }
    const raw = cellRawValue(row, col.binding);
    if (typeof raw !== 'string' || raw === '') {
      return null;
    }
    return col.badgeTones[raw] ?? null;
  }

  // ─── Column width helper ───
  getColWidth(col: GridColumn): string {
    if (!col.width || col.width === '*') return 'auto';
    if (typeof col.width === 'number') return col.width + 'px';
    return col.width;
  }
}
